import React, { useState, useEffect } from 'react';
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Divider,
  Container,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  People as CoachesIcon,
  Group as PlayersIcon,
  AdminPanelSettings as AdminIcon,
  School as AcademyIcon,
  Assessment as ReportsIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  EventAvailable as AttendanceIcon,
} from '@mui/icons-material';
import {
  Collapse,
} from '@mui/material';
import { useAuthStore } from '@store/authStore';
import { useNavigate, useLocation } from 'react-router-dom';
import CoachManagement from '@components/academy/CoachManagement';
import PlayerManagement from '@components/academy/PlayerManagement';
import AdminManagement from '@components/academy/AdminManagement';
import AcademyProfile from '@components/academy/AcademyProfile';
import AcademyDashboard from '@components/academy/AcademyDashboard';
import AcademyReportsPage from '@pages/AcademyReportsPage';
import AcademyAttendancePage from '@pages/AcademyAttendancePage';
import AcademyAttendanceReportPage from '@pages/AcademyAttendanceReportPage';
import { orgTypeLabel } from '@utils/orgTypes';
import SidebarAccountFooter from '@components/layout/SidebarAccountFooter';
import BillingInvoicesPanel from '@components/billing/BillingInvoicesPanel';

const DRAWER_WIDTH = 280;

const AcademyAdminDashboard: React.FC = () => {
  const { user, logout } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [academyInfo, setAcademyInfo] = useState<any>(null);
  const [activeTab, setActiveTab] = useState<string>('dashboard');
  const [reportsExpanded, setReportsExpanded] = useState<boolean>(false);
  const [attendanceExpanded, setAttendanceExpanded] = useState<boolean>(false);

  // Debug: Log user object on mount
  useEffect(() => {
    console.log('[AcademyAdminDashboard] User object:', user);
    console.log('[AcademyAdminDashboard] Academy ID:', (user as any)?.academyId);
    
    // If academyId is missing, try to fetch it from the backend
    if (user && (user.role === 'academy_admin' || user.role === 'ACADEMY_ADMIN') && !(user as any)?.academyId) {
      console.log('[AcademyAdminDashboard] academyId missing, fetching from backend...');
      fetchUserAcademyId();
    }
  }, [user]);

  const fetchUserAcademyId = async () => {
    if (!user?.id) return;
    
    try {
      const response = await fetch(`/api/auth/me?userId=${user.id}`);
      if (response.ok) {
        const result = await response.json();
        if (result.success && result.data?.academyId) {
          console.log('[AcademyAdminDashboard] Found academyId from backend:', result.data.academyId);
          // Update user in auth store
          const updatedUser = { ...user, academyId: result.data.academyId };
          const { setUser } = useAuthStore.getState();
          setUser(updatedUser);
          
          // Also update localStorage
          localStorage.setItem('user', JSON.stringify(updatedUser));
        } else {
          console.warn('[AcademyAdminDashboard] academyId not found in backend response:', result);
        }
      } else {
        console.error('[AcademyAdminDashboard] Failed to fetch user info:', response.status, response.statusText);
      }
    } catch (err) {
      console.error('[AcademyAdminDashboard] Error fetching user academy ID:', err);
    }
  };

  // Redirect if not academy_admin
  useEffect(() => {
    if (user && user.role !== 'academy_admin' && user.role !== 'ACADEMY_ADMIN') {
      console.log('[AcademyAdminDashboard] User is not academy_admin, redirecting. Role:', user.role);
      navigate('/dashboard');
    }
  }, [user, navigate]);

  useEffect(() => {
    // Determine active tab from location
    if (location.pathname.includes('/invoices')) {
      setActiveTab('invoices');
      setReportsExpanded(false);
      setAttendanceExpanded(false);
    } else if (location.pathname.includes('/profile')) {
      setActiveTab('profile');
      setReportsExpanded(false);
    } else if (location.pathname.includes('/coaches')) {
      setActiveTab('coaches');
      setReportsExpanded(false);
    } else if (location.pathname.includes('/players')) {
      setActiveTab('players');
      setReportsExpanded(false);
    } else if (location.pathname.includes('/admins')) {
      setActiveTab('admins');
      setReportsExpanded(false);
    } else if (location.pathname.includes('/reports')) {
      setActiveTab('reports');
      setReportsExpanded(true);
      setAttendanceExpanded(false);
    } else if (location.pathname.includes('/attendance')) {
      setActiveTab('attendance');
      setAttendanceExpanded(true);
      setReportsExpanded(false);
    } else {
      setActiveTab('dashboard');
      setReportsExpanded(false);
      setAttendanceExpanded(false);
    }

    // Load academy info
    if ((user as any)?.academyId) {
      loadAcademyInfo();
    }
  }, [location.pathname, user]);

  const loadAcademyInfo = async () => {
    const academyId = (user as any)?.academyId;
    
    if (!academyId) {
      console.error('[AcademyAdminDashboard] Academy ID not found in user object:', user);
      setError('Academy ID not found. Please contact support.');
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`/api/academies/${academyId}`);
      const result = await response.json();
      
      if (result.success) {
        setAcademyInfo(result.data);
        setError(null);
      } else {
        setError(result.message || 'Failed to load academy information');
      }
    } catch (err: any) {
      console.error('Error loading academy info:', err);
      setError('Failed to load academy information');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const isActive = (path: string) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
    if (tab === 'dashboard') {
      navigate('/academy-admin/dashboard');
    } else if (tab === 'profile') {
      navigate('/academy-admin/profile');
    } else if (tab === 'coaches') {
      navigate('/academy-admin/coaches');
    } else if (tab === 'players') {
      navigate('/academy-admin/players');
    } else if (tab === 'admins') {
      navigate('/academy-admin/admins');
    } else if (tab === 'reports') {
      navigate('/academy-admin/reports');
    } else if (tab === 'attendance') {
      navigate('/academy-admin/attendance');
    }
  };

  const drawer = (
    <Box sx={{ bgcolor: '#e8f4f8', height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Toolbar>
        <Typography variant="h6" noWrap component="div" sx={{ fontWeight: 600, color: '#1976d2' }}>
          Academy Admin
        </Typography>
      </Toolbar>
      <Divider />
      
      {academyInfo && (
        <Box sx={{ p: 2, bgcolor: '#d4e8f5' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
            {academyInfo.name}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {orgTypeLabel(academyInfo.academy_type)}
          </Typography>
        </Box>
      )}
      
      <Divider />
      
      <List sx={{ flexGrow: 1, pt: 1 }}>
        <ListItem disablePadding>
          <ListItemButton
            selected={activeTab === 'dashboard'}
            onClick={() => handleTabChange('dashboard')}
            sx={{
              color: activeTab === 'dashboard' ? '#1976d2' : 'inherit',
              '&.Mui-selected': {
                bgcolor: '#e3f2fd',
                borderLeft: '3px solid #1976d2',
              },
            }}
          >
            <ListItemIcon sx={{ color: activeTab === 'dashboard' ? '#1976d2' : 'inherit' }}>
              <DashboardIcon />
            </ListItemIcon>
            <ListItemText primary="Dashboard" />
          </ListItemButton>
        </ListItem>

        <ListItem disablePadding>
          <ListItemButton
            selected={activeTab === 'admins'}
            onClick={() => handleTabChange('admins')}
            sx={{
              color: activeTab === 'admins' ? '#1976d2' : 'inherit',
              '&.Mui-selected': {
                bgcolor: '#e3f2fd',
                borderLeft: '3px solid #1976d2',
              },
            }}
          >
            <ListItemIcon sx={{ color: activeTab === 'admins' ? '#1976d2' : 'inherit' }}>
              <AdminIcon />
            </ListItemIcon>
            <ListItemText primary="Admins" />
          </ListItemButton>
        </ListItem>

        <ListItem disablePadding>
          <ListItemButton
            selected={activeTab === 'coaches'}
            onClick={() => handleTabChange('coaches')}
            sx={{
              color: activeTab === 'coaches' ? '#1976d2' : 'inherit',
              '&.Mui-selected': {
                bgcolor: '#e3f2fd',
                borderLeft: '3px solid #1976d2',
              },
            }}
          >
            <ListItemIcon sx={{ color: activeTab === 'coaches' ? '#1976d2' : 'inherit' }}>
              <CoachesIcon />
            </ListItemIcon>
            <ListItemText primary="Coaches" />
          </ListItemButton>
        </ListItem>

        <ListItem disablePadding>
          <ListItemButton
            selected={activeTab === 'players'}
            onClick={() => handleTabChange('players')}
            sx={{
              color: activeTab === 'players' ? '#1976d2' : 'inherit',
              '&.Mui-selected': {
                bgcolor: '#e3f2fd',
                borderLeft: '3px solid #1976d2',
              },
            }}
          >
            <ListItemIcon sx={{ color: activeTab === 'players' ? '#1976d2' : 'inherit' }}>
              <PlayersIcon />
            </ListItemIcon>
            <ListItemText primary="Players/Students" />
          </ListItemButton>
        </ListItem>

        <ListItem disablePadding>
          <ListItemButton
            onClick={() => setReportsExpanded(!reportsExpanded)}
            sx={{
              color: activeTab === 'reports' ? '#1976d2' : 'inherit',
            }}
          >
            <ListItemIcon sx={{ color: activeTab === 'reports' ? '#1976d2' : 'inherit' }}>
              <ReportsIcon />
            </ListItemIcon>
            <ListItemText primary="Reports" />
            {reportsExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </ListItemButton>
        </ListItem>
        <Collapse in={reportsExpanded} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            <ListItemButton
              sx={{ pl: 4 }}
              selected={location.pathname.includes('/reports/player-wise')}
              onClick={() => navigate('/academy-admin/reports/player-wise')}
            >
              <ListItemText primary="Player-wise" />
            </ListItemButton>
            <ListItemButton
              sx={{ pl: 4 }}
              selected={location.pathname.includes('/reports/sport-wise')}
              onClick={() => navigate('/academy-admin/reports/sport-wise')}
            >
              <ListItemText primary="Sport-wise" />
            </ListItemButton>
          </List>
        </Collapse>

        <ListItem disablePadding>
          <ListItemButton
            onClick={() => setAttendanceExpanded(!attendanceExpanded)}
            sx={{
              color: activeTab === 'attendance' ? '#1976d2' : 'inherit',
            }}
          >
            <ListItemIcon sx={{ color: activeTab === 'attendance' ? '#1976d2' : 'inherit' }}>
              <AttendanceIcon />
            </ListItemIcon>
            <ListItemText primary="Attendance" />
            {attendanceExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </ListItemButton>
        </ListItem>
        <Collapse in={attendanceExpanded} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            <ListItemButton
              sx={{ pl: 4 }}
              selected={location.pathname === '/academy-admin/attendance'}
              onClick={() => navigate('/academy-admin/attendance')}
            >
              <ListItemText primary="Entry" />
            </ListItemButton>
            <ListItemButton
              sx={{ pl: 4 }}
              selected={location.pathname.includes('/attendance/report')}
              onClick={() => navigate('/academy-admin/attendance/report')}
            >
              <ListItemText primary="Report" />
            </ListItemButton>
          </List>
        </Collapse>
      </List>

      <SidebarAccountFooter
        variant="light"
        onLogout={handleLogout}
        links={{
          profile: '/academy-admin/profile',
          subscriptions: '/academy-admin/dashboard?tab=subscription',
          invoices: '/academy-admin/invoices',
        }}
      />
    </Box>
  );

  const renderContent = () => {
    const academyId = (user as any)?.academyId;
    
    if (!academyId) {
      return (
        <Alert severity="error" sx={{ mb: 2 }}>
          Academy ID not found. Please log out and log back in, or contact support.
          <br />
          <small>User ID: {user?.id}, Role: {user?.role}</small>
        </Alert>
      );
    }
    
    if (loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <CircularProgress />
        </Box>
      );
    }

    if (error) {
      return <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>;
    }

    if (location.pathname.includes('/invoices')) {
      return <BillingInvoicesPanel />;
    }

    // Handle reports routes
    if (location.pathname.includes('/reports')) {
      return <AcademyReportsPage academyId={academyId} />;
    }
    
    // Handle attendance routes
    if (location.pathname.includes('/attendance/report')) {
      return <AcademyAttendanceReportPage academyId={academyId} />;
    }
    if (location.pathname.includes('/attendance')) {
      return <AcademyAttendancePage academyId={academyId} />;
    }
    
    switch (activeTab) {
      case 'profile':
        return <AcademyProfile academyId={academyId} academyInfo={academyInfo} onUpdate={loadAcademyInfo} />;
      case 'coaches':
        return <CoachManagement academyId={academyId} />;
      case 'players':
        return <PlayerManagement academyId={academyId} />;
      case 'admins':
        return <AdminManagement academyId={academyId} />;
      case 'dashboard':
      default:
        return <AcademyDashboard academyId={academyId} academyInfo={academyInfo} />;
    }
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <Drawer
        variant="permanent"
        sx={{
          width: DRAWER_WIDTH,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: DRAWER_WIDTH,
            boxSizing: 'border-box',
          },
        }}
      >
        {drawer}
      </Drawer>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${DRAWER_WIDTH}px)` },
        }}
      >
        <Container maxWidth="xl">
          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
          {renderContent()}
        </Container>
      </Box>
    </Box>
  );
};

export default AcademyAdminDashboard;

